import React, { useState, useEffect } from 'react';
import api, { useAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
    Trash2, AlertTriangle, Database, Server, RefreshCw, Shield, Users,
    FileText, DollarSign, Phone, Calendar, Loader2,
} from 'lucide-react';
import logger from '@/lib/logger';
import { NlpSettingsCard } from './admin-settings/NlpSettingsCard';
import { DigestSettingsCard } from './admin-settings/DigestSettingsCard';
import { FeatureFlagsCard } from './admin-settings/FeatureFlagsCard';

const AdminSettingsPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [dataStats, setDataStats] = useState(null);
    const [environment, setEnvironment] = useState(null);

    // Reset All Data state
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [resetPassword, setResetPassword] = useState('');
    const [resetConfirmText, setResetConfirmText] = useState('');
    const [resetting, setResetting] = useState(false);

    // Module Delete state
    const [moduleStats, setModuleStats] = useState(null);
    const [selectedModules, setSelectedModules] = useState([]);
    const [showModuleDeleteDialog, setShowModuleDeleteDialog] = useState(false);
    const [moduleDeletePassword, setModuleDeletePassword] = useState('');
    const [moduleDeleteConfirm, setModuleDeleteConfirm] = useState('');
    const [deletingModules, setDeletingModules] = useState(false);

    const fetchData = async () => {
        try {
            const [statsRes, envRes, moduleStatsRes] = await Promise.all([
                api.get('/admin/data-stats').catch(e => { logger.warn('Stats error', e); return { data: null }; }),
                api.get('/admin/environment').catch(e => { logger.warn('Env error', e); return { data: null }; }),
                api.get('/admin/module-stats').catch(e => { logger.warn('Module stats error', e); return { data: null }; }),
            ]);
            setDataStats(statsRes.data);
            setEnvironment(envRes.data);
            setModuleStats(moduleStatsRes.data);
        } catch (error) { logger.error('Admin fetch error', error); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleResetData = async () => {
        if (resetConfirmText !== 'RESET ALL DATA') return;
        setResetting(true);
        try {
            const response = await api.post('/admin/reset-data', { password: resetPassword, confirm_text: resetConfirmText });
            toast.success(response.data.message || 'Data reset successfully');
            setShowResetDialog(false);
            setResetPassword('');
            setResetConfirmText('');
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reset data'); }
        finally { setResetting(false); }
    };

    const handleModuleDelete = async () => {
        if (moduleDeleteConfirm !== 'DELETE SELECTED DATA') return;
        setDeletingModules(true);
        try {
            const res = await api.post('/admin/delete-module-data', { modules: selectedModules, password: moduleDeletePassword, confirm_text: moduleDeleteConfirm });
            toast.success(res.data.message || 'Module data deleted');
            setShowModuleDeleteDialog(false);
            setModuleDeletePassword('');
            setModuleDeleteConfirm('');
            setSelectedModules([]);
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete module data'); }
        finally { setDeletingModules(false); }
    };

    const toggleModule = (key) => {
        setSelectedModules(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }
    
    return (
        <div className="space-y-6" data-testid="admin-settings-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <Shield className="h-8 w-8 text-purple-500" />
                        Admin Settings
                    </h1>
                    <p className="text-muted-foreground">System administration and environment management</p>
                </div>
                <Button variant="outline" onClick={fetchData}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
            </div>
            
            {/* Environment Info */}
            <Card className="border-blue-500/30 bg-blue-500/5">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Server className="h-5 w-5 text-blue-500" />
                        <CardTitle>Current Environment</CardTitle>
                    </div>
                    <CardDescription>Active environment and database configuration</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 rounded-lg bg-background border">
                            <p className="text-sm text-muted-foreground mb-1">Environment</p>
                            <Badge className={
                                environment?.environment === 'production' ? 'bg-red-500' :
                                environment?.environment === 'testing' ? 'bg-amber-500' : 'bg-green-500'
                            }>{environment?.environment?.toUpperCase()}</Badge>
                        </div>
                        <div className="p-4 rounded-lg bg-background border">
                            <p className="text-sm text-muted-foreground mb-1">Database</p>
                            <code className="text-sm font-mono">{environment?.database}</code>
                        </div>
                        <div className="p-4 rounded-lg bg-background border">
                            <p className="text-sm text-muted-foreground mb-1">Available Environments</p>
                            <div className="flex gap-1">
                                {environment?.available_environments?.map(env => (
                                    <Badge key={env} variant="outline" className="text-xs">{env}</Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Self-contained sub-components */}
            <NlpSettingsCard />
            <DigestSettingsCard />

            {/* Data Statistics */}
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Database className="h-5 w-5 text-emerald-500" />
                        <CardTitle>Data Statistics</CardTitle>
                    </div>
                    <CardDescription>Current data counts in {environment?.database}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { icon: Shield, color: 'purple', label: 'Super Admins', value: dataStats?.users?.super_admin, note: "Protected (won't be deleted)" },
                            { icon: Users, color: 'blue', label: 'Other Users', value: dataStats?.users?.other_users },
                            { icon: FileText, color: 'emerald', label: 'Leads', value: dataStats?.leads },
                            { icon: Users, color: 'amber', label: 'Students', value: dataStats?.students },
                            { icon: DollarSign, color: 'green', label: 'Payments', value: dataStats?.payments },
                            { icon: Users, color: 'pink', label: 'Employees', value: dataStats?.employees },
                            { icon: Phone, color: 'cyan', label: 'Call Logs', value: dataStats?.call_logs },
                            { icon: Calendar, color: 'orange', label: 'Tasks', value: dataStats?.tasks },
                        ].map(({ icon: Icon, color, label, value, note }) => (
                            <div key={label} className={`p-4 rounded-lg bg-${color}-500/10 border border-${color}-500/30`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Icon className={`h-4 w-4 text-${color}-500`} />
                                    <span className="text-sm font-medium">{label}</span>
                                </div>
                                <p className="text-2xl font-bold">{value || 0}</p>
                                {note && <p className="text-xs text-muted-foreground">{note}</p>}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
            
            {/* Selective Module Data Deletion */}
            <Card className="border-red-500/30">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-red-500" />
                        <CardTitle className="text-red-500">Delete Module Data</CardTitle>
                    </div>
                    <CardDescription>Selectively delete data from specific modules. Choose which data to wipe so you can upload fresh data.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {moduleStats ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(moduleStats).map(([key, mod]) => {
                                const isSelected = selectedModules.includes(key);
                                const colors = { sales: 'blue', customer_service: 'emerald', academics: 'amber', finance: 'green', hr: 'purple', marketing: 'pink', qc: 'cyan', notifications: 'slate' };
                                const color = colors[key] || 'slate';
                                return (
                                    <div key={key} onClick={() => toggleModule(key)} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-red-500 bg-red-500/10' : `border-${color}-500/20 bg-${color}-500/5 hover:border-${color}-500/40`}`} data-testid={`module-${key}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <Checkbox checked={isSelected} onCheckedChange={() => toggleModule(key)} className={isSelected ? 'border-red-500 data-[state=checked]:bg-red-500' : ''} data-testid={`module-check-${key}`} />
                                                <div>
                                                    <p className="font-medium text-sm">{mod.label}</p>
                                                    <p className="text-xs text-muted-foreground">{mod.description}</p>
                                                </div>
                                            </div>
                                            <Badge variant={mod.total > 0 ? 'default' : 'outline'} className={mod.total > 0 ? 'bg-red-500/80' : ''}>{mod.total.toLocaleString()} records</Badge>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted-foreground text-sm">Loading module data...</div>
                    )}
                    <div className="flex items-center justify-between pt-2">
                        <div className="text-sm text-muted-foreground">
                            {selectedModules.length > 0
                                ? `${selectedModules.length} module(s) selected — ${selectedModules.map(k => moduleStats?.[k]?.total || 0).reduce((a, b) => a + b, 0).toLocaleString()} records will be deleted`
                                : 'Select modules above to delete their data'}
                        </div>
                        <Button variant="destructive" disabled={selectedModules.length === 0} onClick={() => setShowModuleDeleteDialog(true)} data-testid="delete-modules-btn">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete Selected Data
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Reset All Data */}
            <Card className="border-red-500/30">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        <CardTitle className="text-red-500">Reset All Data</CardTitle>
                    </div>
                    <CardDescription>Nuclear option — clear ALL data from every module. Super Admin accounts will be preserved.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                            <div>
                                <p className="font-medium text-red-500">Warning: This action cannot be undone!</p>
                                <p className="text-sm text-muted-foreground mt-1">This will permanently delete all leads, students, payments, employees, attendance records, call logs, tasks, and all other data. Only Super Admin user accounts will be kept.</p>
                            </div>
                        </div>
                    </div>
                    <Button variant="destructive" onClick={() => setShowResetDialog(true)} data-testid="reset-data-btn">
                        <Trash2 className="h-4 w-4 mr-2" /> Reset All Data
                    </Button>
                </CardContent>
            </Card>

            {/* Feature Flags (self-contained) */}
            <FeatureFlagsCard />

            {/* Module Delete Confirmation Dialog */}
            <Dialog open={showModuleDeleteDialog} onOpenChange={setShowModuleDeleteDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-500"><AlertTriangle className="h-5 w-5" /> Delete Module Data</DialogTitle>
                        <DialogDescription>This will permanently delete data from the selected modules.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-sm font-medium text-red-500 mb-2">The following modules will be wiped:</p>
                            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                                {selectedModules.map(key => (
                                    <li key={key}><span className="font-medium text-foreground">{moduleStats?.[key]?.label}</span>{' — '}{(moduleStats?.[key]?.total || 0).toLocaleString()} records</li>
                                ))}
                            </ul>
                        </div>
                        <div className="space-y-2">
                            <Label>Enter your password to confirm</Label>
                            <Input type="password" value={moduleDeletePassword} onChange={(e) => setModuleDeletePassword(e.target.value)} placeholder="Your password" data-testid="module-delete-password" />
                        </div>
                        <div className="space-y-2">
                            <Label>Type <code className="bg-muted px-1 rounded">DELETE SELECTED DATA</code> to confirm</Label>
                            <Input value={moduleDeleteConfirm} onChange={(e) => setModuleDeleteConfirm(e.target.value)} placeholder="DELETE SELECTED DATA" data-testid="module-delete-confirm" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModuleDeleteDialog(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleModuleDelete} disabled={deletingModules || moduleDeleteConfirm !== 'DELETE SELECTED DATA'} data-testid="confirm-module-delete-btn">
                            {deletingModules ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="h-4 w-4 mr-2" /> Delete {selectedModules.length} Module(s)</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset Data Dialog */}
            <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-500"><AlertTriangle className="h-5 w-5" /> Confirm Data Reset</DialogTitle>
                        <DialogDescription>This action will permanently delete all data except Super Admin accounts.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-sm font-medium text-red-500">The following will be deleted:</p>
                            <ul className="text-sm text-muted-foreground mt-2 list-disc list-inside">
                                <li>All users (except Super Admins)</li><li>All leads and students</li>
                                <li>All payments and commissions</li><li>All employees and HR data</li>
                                <li>All call logs and activities</li><li>All finance data</li>
                            </ul>
                        </div>
                        <div className="space-y-2">
                            <Label>Enter your password to confirm</Label>
                            <Input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Your password" data-testid="reset-password-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Type <code className="bg-muted px-1 rounded">RESET ALL DATA</code> to confirm</Label>
                            <Input value={resetConfirmText} onChange={(e) => setResetConfirmText(e.target.value)} placeholder="RESET ALL DATA" data-testid="reset-confirm-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleResetData} disabled={resetting || resetConfirmText !== 'RESET ALL DATA'} data-testid="confirm-reset-btn">
                            {resetting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting...</> : <><Trash2 className="h-4 w-4 mr-2" /> Reset All Data</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminSettingsPage;
